import { Card, CardContent } from '@/components/ui/card';
import { FileText, Users, ClipboardCheck, Scale, BookOpen, Shield, CheckCircle } from 'lucide-react';

const assessmentComponents = [
  {
    icon: FileText,
    title: 'Written Examination',
    desc: 'Comprehensive assessment of theoretical knowledge, clinical reasoning, and evidence-based decision-making through structured question formats including MCQs, extended matching questions, and clinical scenario analysis.',
    details: [
      'Multiple-choice questions (MCQs)',
      'Extended matching questions (EMQs)',
      'Short answer questions (SAQs)',
      'Clinical scenario-based reasoning',
    ],
  },
  {
    icon: ClipboardCheck,
    title: 'Clinical Scenario Analysis',
    desc: 'Evaluation of candidates\' ability to analyse complex clinical presentations, develop differential diagnoses, and formulate evidence-based management plans.',
    details: [
      'Case-based clinical presentations',
      'Differential diagnosis formation',
      'Treatment planning and justification',
      'Risk assessment and complication management',
    ],
  },
  {
    icon: Users,
    title: 'Viva Voce',
    desc: 'Structured oral examination assessing professional judgment, clinical reasoning depth, communication skills, and the ability to articulate and defend clinical decisions.',
    details: [
      'Structured questioning by expert examiners',
      'Assessment of clinical reasoning process',
      'Professional communication evaluation',
      'Ethical decision-making scenarios',
    ],
  },
  {
    icon: Scale,
    title: 'OSCE (Objective Structured Clinical Examination)',
    desc: 'Standardised practical assessment using simulated clinical stations to evaluate hands-on competence, procedural skills, patient interaction, and clinical judgment under controlled conditions.',
    details: [
      'Multiple standardised clinical stations',
      'Simulated patient interactions',
      'Procedural skill demonstration',
      'Structured marking criteria',
    ],
  },
  {
    icon: BookOpen,
    title: 'Portfolio Review',
    desc: 'Systematic evaluation of documented clinical experience, case logs, professional development activities, and evidence of continued learning and competency maintenance.',
    details: [
      'Case log documentation review',
      'Evidence of clinical experience breadth',
      'Professional development records',
      'Reflective practice documentation',
    ],
  },
  {
    icon: Shield,
    title: 'Professional Conduct Assessment',
    desc: 'Evaluation of adherence to professional ethics, patient safety standards, informed consent practices, and compliance with regulatory and professional codes of conduct.',
    details: [
      'Ethical practice scenarios',
      'Patient safety awareness',
      'Informed consent principles',
      'Professional boundaries assessment',
    ],
  },
];

export default function ExaminationFrameworkPage() {
  return (
    <div data-testid="examination-framework-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">Assessment Methodology</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">Examination Framework</h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
          <p className="text-lg text-slate-300 mt-4 max-w-3xl">
            IMCAC employs a multi-component assessment framework designed to comprehensively evaluate clinical competency, professional judgment, and ethical practice.
          </p>
        </div>
      </section>

      <section className="imcac-section bg-white">
        <div className="imcac-container">
          <h2 className="text-2xl font-semibold text-[#1B2A4A] font-heading mb-8">Assessment Components</h2>
          <div className="space-y-6">
            {assessmentComponents.map((component, i) => (
              <Card key={i} className="imcac-card" data-testid={`assessment-component-${i}`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#1B2A4A] rounded-sm flex items-center justify-center flex-shrink-0">
                      <component.icon className="h-6 w-6 text-[#C5A572]" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-2">{component.title}</h3>
                      <p className="text-sm text-slate-600 leading-relaxed mb-4">{component.desc}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {component.details.map((detail, j) => (
                          <div key={j} className="flex items-start gap-2">
                            <CheckCircle className="h-4 w-4 text-[#C5A572] flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-slate-700">{detail}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="imcac-section bg-[#F8FAFC]">
        <div className="imcac-container">
          <div className="bg-white border border-slate-200 rounded-sm p-6 md:p-8">
            <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-4">Assessment Integrity</h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              All IMCAC examinations are developed and administered independently from educational providers and training organisations. Examination content is created by subject matter experts and reviewed by the Examination Committee. Standard setting is performed using internationally recognised psychometric methods. IMCAC maintains complete separation between examination activities and any educational or training providers.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
